<template>
  <section class="engagement-container" aria-labelledby="engagement-heading">
    <div class="engagement-header">
      <h2 id="engagement-heading" class="text-h2">
        Engagement
      </h2>
      <TableModal
        :show="engagementDialog"
        :pagination="{ totalPages: usersModalTotalPages, page: usersModalPage }"
        title="Engagement"
        :search-term="search"
        @set-show="engagementDialog = $event"
        @update-page="usersModalPage = $event"
        @close="engagementDialog = false; search = ''; usersModalPage = 1;"
        @update-search="search = $event; usersModalPage = 1;"
      >
        <template #activator="{ props }">
          <DpplAutoSizedButton
            :height="36"
            :width="70"
            color="white"
            class="view-all-button"
            v-bind="props"
          >
            View All
          </DpplAutoSizedButton>
        </template>
        <template #table>
          <EngagementUsersTable
            :items="activeEngagementUsers"
            :headers="headers"
            :page="usersModalPage"
            :sort-by="sortBy"
            :sort-desc="sortDesc"
            @sort-change="setSort"
          />
        </template>
        <template #action>
          <DpplAutoSizedButton
            light
            class="export-button"
            tag="a"
            color="bgDark"
            @click="downloadCSV"
          >
            Export
            <DownloadArrow class="ml-2" />
          </DpplAutoSizedButton>
        </template>
      </TableModal>
    </div>
    <v-table
      class="engagement-table"
    >
      <template #default>
        <thead>
          <tr>
            <th class="engagement-name-table-header">
              Name
            </th>
            <th class="engagement-table-header">
              Average Score
            </th>
            <th class="engagement-table-header">
              Total Revisions
            </th>
            <th class="engagement-table-header edited">
              Posts Edited
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="user in topFiveEngagementUsers"
            :key="user.name"
            class="engagement-row"
          >
            <td
              class="engagement-name"
            >
              <span @click="filterByUser({ userId: user.id, name: user.name })">
                {{ user.name }}
              </span>
            </td>
            <td
              class="engagement-score"
              :style="{ color: getColor(user.avgScore) }"
            >
              {{ user.avgScore }}
            </td>
            <td class="engagement-total-revisions">
              {{ user.totalRevisions }}
            </td>
            <td class="engagement-posts-edited">
              {{ user.postsEdited }}
            </td>
          </tr>
        </tbody>
      </template>
    </v-table>
  </section>
</template>

<script>
import DpplAutoSizedButton from '@/components/datapeople/DpplAutoSizedButton.vue';
import { mapState, mapActions } from 'pinia';
import { useUsage } from '@/stores/insights/usage';
import { assignUsersToCSVDownloadLink } from '@/helpers/fileDownload';
import { colorMap } from '@/helpers/formatters';
import TableModal from '@/components/shared/TableModal.vue';
import { useFilters } from '@/stores/filters/filters';
import { useDatePicker } from '@/stores/filters/datePicker';
import { useColors } from '@/composables/colors';
import { useAppAuthData } from '@/stores/app/authData';
import DownloadArrow from '@/assets/icon-download-arrow.svg?component';
import EngagementUsersTable from './EngagementUsersTable.vue';

export default {
  name: 'UsageEngagement',
  components: {
    DpplAutoSizedButton,
    TableModal,
    EngagementUsersTable,
    DownloadArrow,
  },
  setup() {
    const {
      grey800,
      red700,
      orange700,
      blue700,
      green700,
    } = useColors();

    return {
      colorsToMap: {
        grey800,
        red700,
        orange700,
        blue700,
        green700,
      },
    };
  },
  data() {
    return {
      engagementDialog: false,
      usersModalPage: 1,
      search: '',
      sortBy: 'avgScore',
      sortDesc: true,
      headers: [
        {
          title: 'User Name',
          align: 'start',
          sortable: true,
          value: 'name',
        },
        {
          title: 'Average Score',
          align: 'end',
          sortable: true,
          value: 'avgScore',
        },
        {
          title: 'Total Revisions',
          align: 'end',
          sortable: true,
          value: 'totalRevisions',
        },
        {
          title: 'Posts Edited',
          align: 'end',
          sortable: true,
          value: 'postsEdited',
        },
      ],
    };
  },
  computed: {
    ...mapState(useDatePicker, ['dateRange']),
    ...mapState(useUsage, ['engagementUsers']),
    ...mapState(useAppAuthData, ['authData']),
    usersModalTotalPages() {
      if (this.filteredEngagementUsers?.length) {
        return Math.ceil(this.filteredEngagementUsers.length / 10);
      }
      return 0;
    },
    topFiveEngagementUsers() {
      const engagementUsers = this.activeEngagementUsers;
      return engagementUsers.sort((a, b) => {
        if (a.avgScore > b.avgScore) return -1;
        if (a.avgScore < b.avgScore) return 1;
        return 0;
      }).slice(0, 5);
    },
    filteredEngagementUsers() {
      const searchTerm = this.search?.toLowerCase() || '';

      return this.engagementUsers.filter(
        (user) => !searchTerm || user.name?.toLowerCase().includes(searchTerm),
      );
    },
    engagementUsersWithColor() {
      return this.filteredEngagementUsers
        .map((user) => ({ ...user, color: this.getColor(user.avgScore) }));
    },
    activeEngagementUsers() {
      return this.engagementUsersWithColor.filter((user) => user.postsEdited);
    },
  },
  watch: {
    engagementDialog(newValue) {
      if (newValue) {
        this.usersModalPage = 1;
      }
    },
  },
  methods: {
    ...mapActions(useFilters, ['setUserFilter']),
    filterByUser({ userId, name }) {
      this.setUserFilter({ router: this.$router, user: { id: userId, label: name } });
    },
    getColor(score) {
      return colorMap(score, this.colorsToMap);
    },
    downloadCSV() {
      const {
        filteredEngagementUsers, dateRange, authData, sortDesc, sortBy,
      } = this;
      assignUsersToCSVDownloadLink({
        engagementUsers: filteredEngagementUsers, dateRange, projectName: authData.projectName, querySelector: '.export-button', sortDesc, sortBy,
      });
    },
    setSort(event) {
      if (event?.[0]) {
        this.sortBy = event[0].key;
        this.sortDesc = event[0].order === 'desc';
        this.usersModalPage = 1;
      }
    },
  },
};
</script>

<style lang="less" scoped>
@import '@/less/mixins';

.engagement-container {
  background: @white;
  width: 354px;
  height: 100%;
  padding: 16px;
  border-radius: 8px;
  border: 1px solid @stroke-default;

}

.engagement-header {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  h2 {
    text-align: left;
    line-height: 30px;
  }
}

/* =====================
    View All Button
  ===================== */

.view-all-button.v-btn {
  border: 1px solid @stroke-default !important;
  background-color: @white;

  :deep(.v-btn__content) {
    font-size: 12px;
    font-weight: 400;
    color: @text-darkest;
    text-transform: capitalize;
  }

  &:hover, &:focus, &:active {
    background-color: @white !important;

    :deep(.v-btn__overlay) {
      display: none;
    }
  }

  &::before {
    transition: none;
  }

  &:focus {
    border: 1px solid @stroke-default !important;

    &::before {
      background: @white !important;
    }

    :deep(.v-btn__content) {
      color: @text-darkest;
    }
  }

  &:hover {
    border: 1px solid @brand-primary !important;

    &::before {
      background: @white !important;
    }

    :deep(.v-btn__content) {
      color: @text-brand;
    }
  }
}

/* =====================
    Engagement Panel Table
  ===================== */

.engagement-table {
  display: flex;
  width: 100%;

  tr:hover {
    background-color: transparent !important;
  }

  :deep(.v-table__wrapper){
    flex-grow: 1;
  }

  &.v-table :deep(th.engagement-name-table-header) {
    flex: 1;
    width: auto;
    text-align: left;
    font-weight: 300;
    line-height: 14px;
    height: 42px;
    padding: 0;
    color: @text-dark;
    font-size: 12px;
  }

  &.v-table :deep(th.engagement-table-header) {
    width: 21%;
    flex: 0 auto;
    text-align: right !important;
    padding: 0;
    font-size: 12px;
    font-weight: 300;
    line-height: 14px;
    height: 42px;
    color: @text-dark;
  }

  &.v-table tbody {
    vertical-align: top;
  }

  td.engagement-name {
    font-weight: 300;
    font-size: 12px;
    width: auto;
    flex: 1;
    text-align: left;

    & span {
      color: @text-darkest;
      cursor: pointer;
    }

    & span:hover {
      text-decoration: underline;
    }
  }

  td.engagement-score {
    font-weight: 500;
    text-align: right;
    flex: 0 auto;
  }

  td.engagement-total-revisions {
    color: @text-brand;
    font-weight: 500;
    text-align: right;
    flex: 0 auto;
  }

  td.engagement-posts-edited {
    color: @text-brand;
    font-weight: 500;
    text-align: right;
    flex: 0 auto;
  }

  td.engagement-score, td.engagement-blue-metric {
    flex: 0 auto;
    vertical-align: middle;
    max-height: 53px;
    height: 53px;
    flex: 0 auto;
  }

  tr.engagement-row {
    td {
      height: 40px;
      max-height: 40px;
      vertical-align: middle;
      padding: 0;
    }
  }
}

.export-button :deep(.v-btn__content) {
  font-weight: 400;
  font-size: 12px;
  line-height: 14px;
  text-transform: capitalize;
  letter-spacing: normal;
}

.export-button:hover {
  background-color: @text-brand !important;
}

.export-button:hover :deep(.v-btn__content) {
  color: white;

  svg, path {
    background: transparent !important;
    fill: white !important;
  }
}

.export-button:hover :deep(.v-btn__overlay) {
  display: none;
}
</style>
